import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {ContestStatus, ModalType, RequestState, WinnerCount} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {
	IContestPlayer,
	IContestPlayersStore,
} from "data/stores/contest_players/contest_players.store";
import {ChangeEvent} from "react";
import {ALLOWED_LANGUAGES, INTERVAL, SPECIAL_SYMBOLS} from "data/constants";
import {forEach, includes} from "lodash";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IContest, IContestsStore} from "data/stores/contests/contests.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IGamesStore} from "data/stores/games/games.store";
import type {IUserStore} from "data/stores/user/user.store";
import {useNavigate} from "react-router-dom";
import {IPrediction} from "data/providers/api/predictions.api.provider";
import {changeRouteLanguage, getLanguageFromPath} from "data/utils/language";
import {AnalyticService, IAdobeDTM} from "data/utils/track_analytics";
import type {IStaticContentStore, TArticle} from "data/stores/static_content/static_content.store";

interface IProps {
	gameFeedId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IPlayersController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get isLoading(): boolean;
	get players(): IContestPlayer[];
	get search(): string;
	get currentContest(): IContest | undefined;
	get winnerPlayer(): IContestPlayer | null | undefined;
	get opponentWinnerPlayer(): IContestPlayer | null | undefined;
	get winnersCount(): WinnerCount;
	get squadByPlayer(): ISquad | null | undefined;
	get isCurrentContestLive(): boolean;
	get isCurrentContestResulted(): boolean;
	get currentContestId(): number;
	get currentContestStatus(): ContestStatus;
	get currentLanguage(): string;
	get secondLanguage(): string;
	get faqContent(): TArticle[] | undefined;

	updateSearch: (e: ChangeEvent<HTMLInputElement>) => void;
	toLeaderboard: () => void;
	languageToggle: () => void;
}

@injectable()
export class PlayersController implements IPlayersController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _search: string = "";
	@observable _gameFeedId: number = 0;
	private _disposerReaction!: ReturnType<typeof reaction>;
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable protected _contestPlayersDisposer?: ReturnType<typeof reaction>;
	private _intervalDisposerId: ReturnType<typeof setInterval> | null = null;

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}
	get currentContest() {
		return this._contestsStore.getContestByFeedId(this._gameFeedId);
	}

	get currentContestId() {
		if (!this.currentContest) return 0;

		return this.currentContest.id;
	}

	get currentContestStatus() {
		if (!this.currentContest) return ContestStatus.SCHEDULED;

		return this.currentContest.status;
	}

	get isCurrentContestLive() {
		return this.currentContestStatus === ContestStatus.LIVE;
	}

	get isCurrentContestResulted() {
		return this.currentContestStatus === ContestStatus.RESULTED;
	}

	get winnerPlayer() {
		if (this.currentContest?.winnerId) {
			return this._contestPlayersStore.getPlayerById(this.currentContest.winnerId);
		}

		return null;
	}

	get opponentWinnerPlayer() {
		if (this.currentContest?.opponentWinnerId) {
			return this._contestPlayersStore.getPlayerById(this.currentContest.opponentWinnerId);
		}

		return null;
	}

	get winnersCount() {
		if (this.winnerPlayer && this.opponentWinnerPlayer) {
			if (this.winnerPlayer.playerId === this.opponentWinnerPlayer.playerId) {
				return WinnerCount.EQUAL;
			} else {
				return WinnerCount.DOUBLE;
			}
		}

		return WinnerCount.SINGLE;
	}

	get trackData(): IAdobeDTM["trackState"] {
		const pageWinnerName = "Major League Baseball: Player of the Game: Winner";

		if (this.isCurrentContestResulted) {
			if (this.winnerPlayer?.playerId && this.opponentWinnerPlayer?.playerId) {
				if (this.winnerPlayer.playerId === this.opponentWinnerPlayer.playerId) {
					return {
						"page.legacyName": pageWinnerName,
						"baseball.playerId": String(this.winnerPlayer.feedId),
					};
				} else {
					return {
						"page.legacyName": pageWinnerName,
						"baseball.playerId": `${this.winnerPlayer.feedId}|${this.opponentWinnerPlayer.feedId}`,
					};
				}
			} else {
				return {
					"page.legacyName": pageWinnerName,
					"baseball.playerId": String(this.winnerPlayer?.feedId),
				};
			}
		}

		return {
			"page.legacyName": "Major League Baseball: Player of the Game: Vote",
			"baseball.gamePk": String(this._gameFeedId),
		};
	}

	get squadByPlayer() {
		if (!this.winnerPlayer) return null;

		return this._squadsStore.getSquadById(this.winnerPlayer.squadId);
	}

	get players() {
		return this._contestPlayersStore.list.filter((player) => {
			let fullName = player.name.toLowerCase();
			let enteredName = this.search.toLowerCase();

			forEach(SPECIAL_SYMBOLS, (item, key) => {
				fullName = fullName.replace(key, item);
				enteredName = enteredName.replace(key, item);
			});

			return fullName.includes(enteredName);
		});

		// return orderBy(playersFilteredBySearch, ["isFeatured", "winProbability"], ["desc", "desc"]);
	}

	get search() {
		return this._search;
	}

	get currentLanguage() {
		return !ALLOWED_LANGUAGES.includes(getLanguageFromPath()) || getLanguageFromPath() === "en"
			? "en"
			: "es";
	}

	get secondLanguage() {
		return !ALLOWED_LANGUAGES.includes(getLanguageFromPath()) || getLanguageFromPath() === "en"
			? "es"
			: "en";
	}

	get faqContent() {
		return this._staticContentStore.articles;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ContestPlayersStore) private _contestPlayersStore: IContestPlayersStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.GamesStore) private _gameStore: IGamesStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.StaticContentStore)
		private readonly _staticContentStore: IStaticContentStore
	) {
		makeAutoObservable(this);
	}

	updateSearch = (e: ChangeEvent<HTMLInputElement>) => {
		const {value} = e.target;
		this._search = value;
	};

	@action fetchPlayersLive = (contestId: number) => {
		if (this.isCurrentContestLive) {
			if (this._intervalDisposerId) {
				clearInterval(this._intervalDisposerId);
			}

			this._intervalDisposerId = setInterval(() => {
				void this._contestPlayersStore.fetchPlayers(contestId);
			}, INTERVAL);
		}
	};

	toLeaderboard = () => {
		this._navigate?.("/leaderboard");
	};

	async fetchContestsPlayers(contestId: number) {
		await this._contestPlayersStore
			.fetchPlayers(contestId)
			.then(() => {
				if (this._userStore.isAuthorized) {
					void this._predictionsStore.fetchPredictionByContest(contestId);
				}
			})
			.catch((error: AxiosError<IApiResponse>) => {
				this._modalStore.showModal(ModalType.ERROR, {
					message: extractErrorMessage(error),
				});
			});
	}

	private showConfirmModal = () => {
		const localPrediction = JSON.parse(
			localStorage.getItem("localPrediction") || "{}"
		) as IPrediction;

		if (localPrediction) {
			this._modalStore.showModal(ModalType.CONFIRM, {
				message: "",
				playerId: localPrediction.playerId,
				contestId: localPrediction.contestId,
			});
		}
	};

	saveLocalPrediction() {
		const localPrediction = JSON.parse(
			localStorage.getItem("localPrediction") || "{}"
		) as IPrediction;

		if (
			this._userStore.isAuthorized &&
			localPrediction.contestId &&
			this.isCurrentContestLive
		) {
			void this._predictionsStore
				.savePrediction({...localPrediction})
				.then(this.showConfirmModal);
			// .catch((err: AxiosError<IApiResponse>) => this.onError(err));
		}
	}

	private onError = (error: AxiosError<IApiResponse>) => {
		const errorMsg = extractErrorMessage(error);

		this._modalStore.showModal(ModalType.ERROR, {
			message: errorMsg,
		});
	};

	public languageToggle = () => {
		const newLanguage = this.currentLanguage === "es" ? "en" : "es";
		changeRouteLanguage(this.currentLanguage, newLanguage);
	};

	dispose() {
		if (this._intervalDisposerId) {
			clearInterval(this._intervalDisposerId);
		}

		this._disposerReaction?.();
		this._contestPlayersDisposer?.();
	}

	async init({gameFeedId, navigate}: IProps) {
		this._gameFeedId = gameFeedId;
		this._navigate = navigate;
		this._requestState = RequestState.PENDING;

		this._predictionsStore.clearSavedState();

		await Promise.all([
			this._contestsStore.fetchContest(),
			this._squadsStore.fetchSquads(),
			this._playersStore.fetchPlayers(),
			this._gameStore.fetchGames(),
			this._staticContentStore.fetchStaticContent(),
		]);

		const contestId = this.currentContest?.id ?? 0;

		if (contestId) {
			this._contestPlayersDisposer = reaction(
				() => this.currentContest?.status,
				(status) => {
					if (includes([ContestStatus.LIVE, ContestStatus.RESULTED], status)) {
						void this.fetchContestsPlayers(contestId).finally(() => {
							runInAction(() => {
								this._requestState = RequestState.SUCCESS;
							});
						});
					}
				},
				{fireImmediately: true}
			);

			this.saveLocalPrediction();

			this.fetchPlayersLive(contestId);
		}

		setTimeout(() => {
			this._requestState = RequestState.SUCCESS;
			AnalyticService.trackState(this.trackData);
		}, 1000);
	}
}
